// src/components/TaxNumbers/TaxFlowExplainerPage.js

import React, { useState } from "react";
import Phone from "../Phone/Phone";
import Header from "../Header/Header";
import TaxNumbersFlow from "./TaxNumbersFlow";
import "./TaxFlowExplainerPage.css";
import MovingTicker from "../Header/MovingTicker";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

const SECTIONS = [
  {
    id: "income",
    code: "I",
    title: "Income",
    body:
      "Your wages, interest income, etc. Everything you earned for the year that the IRS counts. This is the starting point of the flow.",
  },
  {
    id: "aboveLine",
    code: "D",
    title: "Above-the-Line Deductions",
    body:
      "Certain deductions that reduce Adjusted Gross Income (AGI) directly. Examples can include some retirement contributions, HSA contributions, etc - outside of employment",
  },
  {
    id: "agi",
    code: "AGI",
    title: "Adjusted Gross Income (AGI)",
    body:
      "AGI is your income after above-the-line deductions. Many credits and benefits use AGI or a version of it to decide if you qualify.",
  },
  {
    id: "std",
    code: "STD",
    title: "Standard / Below-the-Line Deductions",
    body:
      "Next, you subtract either the standard deduction or itemized deductions. This step shrinks your income again to get taxable income. Most households just use the standard deduction.",
  },
  {
    id: "taxable",
    code: "TI",
    title: "Taxable Income",
    body:
      "Adjusted Gross Income minus Below-the-Line Deductions: This is the slice of your income that is actually taxed after all deductions. Your tax brackets apply to this number & not to your original income.",
  },
  {
    id: "taxBeforeCredits",
    code: "TAX",
    title: "Tax Before Credits",
    body:
      "Taxable Income * Applicable Tax Rate: If there were no credits at all, this is roughly what your income tax would be. It’s like a ‘sticker price’ before discounts.",
  },
  {
    id: "nonref",
    code: "NRC",
    title: "Non-refundable Credits",
    body:
      "Credits that can only bring your income tax down to zero. They cannot create or increase a refund by themselves. The Saver’s Credit lives here — it reduces the tax you owe, but cannot make it go below zero.",
  },
  {
    id: "ref",
    code: "RC",
    title: "Refundable Credits",
    body:
      "Credits that can go beyond zero. If your tax is already wiped out, refundable credits can turn into a refund. Examples include part of the Child Tax Credit and the Earned Income Tax Credit.",
  },
  {
    id: "final",
    code: "FINAL",
    title: "Final Tax or Refund",
    body:
      "Tax Before Credits minus Credits: After credits, withholdings, and other payments are accounted for, you end up with either a balance due or a refund. The goal of this page is not to calculate your personal number, but to show how each step changes the amount.",
  },
];

const BASE_INCOME = 75000;
const STANDARD_DEDUCTION = 15750;
const NONREFUNDABLE_CREDITS = 2000;
const REFUNDABLE_CREDITS = 1500;

// Treat this as rules / parameters, not fixed outcome
const SAMPLE_PAYCHECK = {
  grossWages: BASE_INCOME,
  socialSecurityRate: 0.062, // 6.2% example
  medicareRate: 0.0145, // 1.45% example
  otherWithholding: 2500, // e.g. state, local
};

const TaxFlowExplainerPage = () => {
  const [openId, setOpenId] = useState("income");
  const [aboveLineDeductions, setAboveLineDeductions] = useState(6000);
  const [isEditingD, setIsEditingD] = useState(false);

  const toggleSection = (id) => {
    setOpenId((current) => (current === id ? null : id));
  };

  // ---------- TAX FLOW NUMBERS ----------
  const agi = Math.max(BASE_INCOME - aboveLineDeductions, 0);
  const taxableIncome = Math.max(agi - STANDARD_DEDUCTION, 0);

  // Teaching-only flat rate
  const taxBeforeCredits = Math.round(taxableIncome * 0.2);

  // Nonrefundable credits first
  const afterNonref = Math.max(taxBeforeCredits - NONREFUNDABLE_CREDITS, 0);

  // Then refundable credits
  const finalNumber = afterNonref - REFUNDABLE_CREDITS; // can go negative = refund

  // ---------- PAYCHECK BOX NUMBERS ----------
  // Social Security & Medicare are never affected by credits — only by wages.
  const socialSecurityTax = Math.round(
    SAMPLE_PAYCHECK.grossWages * SAMPLE_PAYCHECK.socialSecurityRate
  );
  const medicareTax = Math.round(
    SAMPLE_PAYCHECK.grossWages * SAMPLE_PAYCHECK.medicareRate
  );

  // Federal tax "before credits" (just mirroring the tax flow)
  const federalTaxBeforeCredits = Math.max(taxBeforeCredits, 0);

  // Total credits being applied to federal tax (teaching picture)
  const totalCredits = NONREFUNDABLE_CREDITS + REFUNDABLE_CREDITS;

  // Effective federal tax AFTER credits (this is what actually leaves the paycheck)
  const federalTaxAfterCredits = Math.max(
    federalTaxBeforeCredits - totalCredits,
    0
  );

  // This is the amount "taken" from the wages box
  const totalWithholding =
    socialSecurityTax +
    medicareTax +
    federalTaxAfterCredits +
    SAMPLE_PAYCHECK.otherWithholding;

  const takeHome = SAMPLE_PAYCHECK.grossWages - totalWithholding;

  return (
    <div>
      <div className="ticker-fixed">
        <MovingTicker>
          <>
            Millions of taxfilers miss out on benefits simply because we didn’t have guidance like Gildarc.
            This isn’t the usual tax education - for that you can visit
            <a
              href="https://apps.irs.gov/app/understandingTaxes/teacher/hows.jsp"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#0f62fe", textDecoration: "underline", marginLeft: "6px" }}
            >
              the IRS Understanding Taxes site
            </a>.
            This explains the stages professionals usually charge money to walk people through.
          </>
        </MovingTicker>
      </div>

      <div className="tax-flow-explainer-page">
        <Header />

        <main className="tax-flow-explainer-main">
          {/* Intro / hero */}
          <section className="tax-flow-explainer-hero">
            <div className="tax-flow-pill">1040 flow · educational only</div>
            <h1>See how income becomes taxes, step by step.</h1>
            <p>
              This isn&apos;t tax prep. It&apos;s a simplified illustration so you
              can see how income turns into taxable income, how deductions shrink
              it, and how credits lower your final tax dollar-for-dollar.
            </p>
            <p className="tax-flow-hero-note">
              When you understand the flow from wages to tax to take-home, then
              you are one step closer.
            </p>
          </section>

          {/* Layout: phone on the left, explanations on the right */}
          <section className="tax-flow-explainer-layout">
            {/* LEFT: Phone + paycheck card */}
            <div className="tax-flow-explainer-phone">
              <Phone>
                <TaxNumbersFlow
                  income={BASE_INCOME}
                  deductions={aboveLineDeductions}
                  agi={agi}
                  standardDeduction={STANDARD_DEDUCTION}
                  taxableIncome={taxableIncome}
                  taxBeforeCredits={taxBeforeCredits}
                  nonrefundableCredits={NONREFUNDABLE_CREDITS}
                  refundableCredits={REFUNDABLE_CREDITS}
                  finalNumber={finalNumber}
                  finalLabel={
                    finalNumber >= 0
                      ? "Estimated Total Taxes (example)"
                      : "Estimated Total Refund (example)"
                  }
                  onAGIClick={() => {
                    setOpenId("agi");
                    setIsEditingD(false);
                  }}
                  onDeductionClick={() => {
                    setOpenId("aboveLine");
                    setIsEditingD(true); // turn D row into an input
                  }}
                  isEditingDeductions={isEditingD}
                  onDeductionsChange={(val) => setAboveLineDeductions(val)}
                />
              </Phone>

              {/* Total funds availability / paycheck-style table */}
              <div className="tax-flow-paycheck-card">
                <div className="tax-flow-paycheck-header">
                  Total funds from wages (example)
                </div>
                <p className="tax-flow-paycheck-intro">
                  This is a simple picture of one year of wages and what happens
                  before the money is fully &quot;yours&quot; to spend.
                </p>

                <table className="tax-flow-paycheck-table">
                  <tbody>
                    <tr>
                      <td className="tax-flow-paycheck-label">Wages earned</td>
                      <td className="tax-flow-paycheck-value">
                        {formatCurrency(SAMPLE_PAYCHECK.grossWages)}
                      </td>
                    </tr>

                    <tr className="tax-flow-paycheck-row-deduction">
                      <td className="tax-flow-paycheck-label">
                        Social Security tax
                      </td>
                      <td className="tax-flow-paycheck-value">
                        −{formatCurrency(socialSecurityTax)}
                      </td>
                    </tr>

                    <tr className="tax-flow-paycheck-row-deduction">
                      <td className="tax-flow-paycheck-label">Medicare tax</td>
                      <td className="tax-flow-paycheck-value">
                        −{formatCurrency(medicareTax)}
                      </td>
                    </tr>

                    <tr className="tax-flow-paycheck-row-deduction">
                      <td className="tax-flow-paycheck-label">
                        Federal income tax (before credits)
                      </td>
                      <td className="tax-flow-paycheck-value">
                        −{formatCurrency(federalTaxBeforeCredits)}
                      </td>
                    </tr>

                    <tr className="tax-flow-paycheck-row-credit">
                      <td className="tax-flow-paycheck-label">
                        Federal credits (reduce taxes)
                      </td>
                      <td className="tax-flow-paycheck-value">
                        +{formatCurrency(totalCredits)}
                      </td>
                    </tr>

                    <tr className="tax-flow-paycheck-row-deduction">
                      <td className="tax-flow-paycheck-label">
                        Federal income tax (after credits)
                      </td>
                      <td className="tax-flow-paycheck-value">
                        −{formatCurrency(federalTaxAfterCredits)}
                      </td>
                    </tr>

                    <tr className="tax-flow-paycheck-row-deduction">
                      <td className="tax-flow-paycheck-label">
                        Other taxes (state, local, etc.)
                      </td>
                      <td className="tax-flow-paycheck-value">
                        −{formatCurrency(SAMPLE_PAYCHECK.otherWithholding)}
                      </td>
                    </tr>

                    <tr className="tax-flow-paycheck-row-total">
                      <td className="tax-flow-paycheck-label">
                        Take-home money for now (example)
                      </td>
                      <td className="tax-flow-paycheck-value">
                        {formatCurrency(takeHome)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <p className="tax-flow-paycheck-note">
                  Credits don&apos;t touch Social Security or Medicare, but they
                  do cut down the federal income tax slice — which increases the
                  money that&apos;s actually available for you.
                </p>

                <div className="tax-flow-paycheck-highlight1">
                  In later modules, we&apos;ll see how two people with the same
                  income can end up with very different results, simply by
                  understanding what exists and how to use it.
                  <br/> <br/>
                  <p>We will also explore strategies that can impact Social Security and Medicare taxes,  showing how the same income can produce very different outcomes
                    </p>  
                
                </div>
              </div>
            </div>

            {/* RIGHT: Flow summary + accordion */}
            <div
              className="tax-flow-explainer-accordion"
              style={{ marginTop: "35px" }}
            >
              <div className="tax-flow-explainer-savers">
                <strong>The Flow</strong>
                <p style={{ textAlign: "left" }}>
                  <strong>In short</strong> : You earn money → some of it is
                  reduced before tax → more is reduced by the standard deduction →
                  the remaining portion is taxed → credits lower that tax →
                  leaving a final bill or refund.
                </p>
                <p style={{ textAlign: "left" }}>
                  The stories in the Modules section take this flow and show how
                  real people use it to keep more of their money, year after year.
                </p>
              </div>

              <div
                className="tax-flow-explainer-savers"
                style={{ marginBottom: "45px" }}
              >
                <strong>
                  Try editing the “Above-the-line Deductions” number in the
                  table.
                </strong>

                <p>
                  It directly affects your Adjusted Gross Income (AGI), which is
                  the starting point for many credits and deductions. In this
                  example, the credits are fixed, but the federal tax slice and
                  the take-home amount both move when AGI changes.
                </p>
              </div>

              <h2>Walk through each step</h2>
              <p className="tax-flow-explainer-subtext">
                Click through the steps to see what each piece means in plain
                language.
              </p>

              <div className="tax-flow-accordion-list">
                {SECTIONS.map((section) => {
                  const isOpen = openId === section.id;

                  return (
                    <div
                      key={section.id}
                      className={`tax-flow-accordion-item ${
                        isOpen ? "tax-flow-accordion-item--open" : ""
                      }`}
                    >
                      <button
                        className="tax-flow-accordion-header"
                        onClick={() => toggleSection(section.id)}
                        type="button"
                      >
                        <span className="tax-flow-accordion-code">
                          {section.code}
                        </span>
                        <span className="tax-flow-accordion-title">
                          {section.title}
                        </span>
                        <span className="tax-flow-accordion-toggle">
                          {isOpen ? "−" : "+"}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="tax-flow-accordion-body">
                          {(() => {
                            const formulaLines = {
                              agi: "Income minus Above-the-Line Deductions:",
                              taxable:
                                "AGI minus Standard/Below-the-Line Deductions:",
                              taxBeforeCredits: "Taxable Income × Tax Rate:",
                              final: "Tax Before Credits minus Credits:",
                            };

                            const formulaText = formulaLines[section.id];

                            if (formulaText) {
                              return (
                                <>
                                  <p className="tax-flow-accordion-body-emphasis">
                                    <strong>{formulaText}</strong>
                                  </p>
                                  <p>{section.body}</p>
                                </>
                              );
                            }

                            return <p>{section.body}</p>;
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="tax-flow-explainer-disclaimer">
                This page is educational only and not tax, legal, or investment
                advice. It&apos;s a mock-up illustration, not a calculation of
                your own tax return.
              </div>
            </div>
          </section>
        </main>

        <footer className="site-footer">
          <div className="footer-disclaimer">
            This platform does not provide tax, legal, or financial advice.
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TaxFlowExplainerPage;
